import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, Package, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../components/ui/confirmation-dialog';

/**
 * DryHireAssetPicker
 * ──────────────────
 * Self-contained asset picker for dry-hire jobs.  Single source of truth used
 * by both the inline drawer panel AND the standalone allocation modal.
 *
 * Responsibilities:
 *   • Fetch equipment list filtered by `job.equipment_type`
 *   • Show "ON HIRE — Job #N" disabled state for assets currently locked by
 *     another active dry-hire (computed from the optional `lockedAssets` map)
 *   • Save through POST /api/allocations (or PUT if existing) — backend
 *     enforces full-span dates so we just send the equipment_id
 *   • Off-Hire shortcut button when the job is on_hire / dispatched
 */
export function DryHireAssetPicker({
  api,
  job,
  existingAllocation,            // optional: existing allocation row to edit
  onSaved,
  onCancel,
  onOffHire,                     // optional: open the Off-Hire dialog
  layout = 'inline',             // 'inline' | 'modal'
}) {
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [equipmentId, setEquipmentId] = useState(existingAllocation?.equipment_id || '');
  const [lockedMap, setLockedMap] = useState({}); // { equipment_id: { job_number, hire_status, end_date } }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // 1) load equipment of this type (or all if equipment_type is unset)
        const params = new URLSearchParams();
        if (job?.equipment_type) params.set('equipment_type', job.equipment_type);
        params.set('limit', '500');
        const eqResp = await api.get(`/equipment?${params.toString()}`);
        const eqItems = Array.isArray(eqResp.data) ? eqResp.data : (eqResp.data?.items || []);
        if (cancelled) return;
        setEquipment(eqItems);

        // 2) load active dry-hires to mark locked assets
        const dh = await api.get('/dry-hires?status=active');
        if (cancelled) return;
        const map = {};
        (dh.data?.items || []).forEach((j) => {
          if (j.id === job?.id) return;                       // ignore self
          const eqId = j.primary_allocation?.equipment_id;
          if (eqId) {
            map[eqId] = {
              job_number: j.job_number,
              hire_status: j.hire_status,
              end_date: j.end_date,
            };
          }
        });
        setLockedMap(map);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load equipment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, job?.id, job?.equipment_type]);

  const sortedEquipment = useMemo(() => {
    const free = []; const locked = [];
    equipment.forEach((eq) => {
      (lockedMap[eq.id] ? locked : free).push(eq);
    });
    free.sort((a, b) => (a.asset_id || '').localeCompare(b.asset_id || ''));
    locked.sort((a, b) => (a.asset_id || '').localeCompare(b.asset_id || ''));
    return [...free, ...locked];
  }, [equipment, lockedMap]);

  const isLocked = (eqId) => Boolean(lockedMap[eqId]);
  const lockedReason = (eqId) => {
    const lk = lockedMap[eqId];
    if (!lk) return null;
    return `ON HIRE · Job #${lk.job_number}${lk.end_date ? ` · ends ${lk.end_date}` : ' · open-ended'}`;
  };

  const save = async () => {
    if (!equipmentId) { toast.error('Pick an asset to allocate'); return; }
    if (isLocked(equipmentId)) {
      toast.error(`Cannot allocate — ${lockedReason(equipmentId)}`); return;
    }
    setSaving(true);
    try {
      if (existingAllocation?.id) {
        await api.put(`/allocations/${existingAllocation.id}`, {
          job_id: job.id,
          operator_id: '',
          equipment_id: equipmentId,
          start_date: job.start_date,
          end_date: job.end_date || null,
          attachment_ids: existingAllocation.attachment_ids || [],
        });
        toast.success('Asset updated');
      } else {
        await api.post('/allocations', {
          job_id: job.id,
          operator_id: '',
          equipment_id: equipmentId,
          start_date: job.start_date,
          end_date: job.end_date || null,
          attachment_ids: [],
          is_combo_allocation: false,
          combo_equipment_ids: [],
        });
        toast.success('Asset allocated');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Allocation failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!existingAllocation?.id) return;
    setConfirmDialog({
      open: true,
      title: 'Remove Allocation',
      description: 'Remove this asset allocation? The asset will become available again.',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        setSaving(true);
        try {
          await api.delete(`/allocations/${existingAllocation.id}`);
          toast.success('Allocation removed');
          onSaved?.();
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Remove failed');
        } finally { setSaving(false); }
      },
    });
  };

  const isActive = ['booked', 'dispatched', 'on_hire'].includes(job?.hire_status);
  const showOffHire = isActive && !!onOffHire && (job?.hire_status === 'on_hire' || job?.hire_status === 'dispatched');

  return (
    <>
    <div className={layout === 'modal' ? 'space-y-4' : 'space-y-3 p-3 rounded-md border border-zinc-800 bg-zinc-900/30'}>
      {layout === 'inline' && (
        <div className="flex items-center justify-between">
          <Label className="label-overline flex items-center gap-2">
            <Package className="h-4 w-4" />
            Allocated Asset
          </Label>
          {existingAllocation?.id && (
            <Badge variant="outline" className="text-[10px] bg-[#9aff00]/10 text-[#9aff00] border-[#9aff00]/40">
              Allocated
            </Badge>
          )}
        </div>
      )}

      <div>
        <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
          Asset {job?.equipment_type && <span className="normal-case opacity-60">· {job.equipment_type}</span>}
        </Label>
        <Select value={equipmentId} onValueChange={setEquipmentId} disabled={loading || saving}>
          <SelectTrigger data-testid="dryhire-asset-picker-trigger" className="w-full">
            <SelectValue placeholder={loading ? 'Loading assets…' : 'Select an asset…'} />
          </SelectTrigger>
          <SelectContent>
            {sortedEquipment.length === 0 && !loading && (
              <div className="px-2 py-3 text-sm text-muted-foreground">No matching assets.</div>
            )}
            {sortedEquipment.map((eq) => {
              const locked = isLocked(eq.id);
              return (
                <SelectItem
                  key={eq.id}
                  value={eq.id}
                  disabled={locked}
                  data-testid={`dryhire-asset-option-${eq.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs">{eq.asset_id || '—'}</span>
                    <span>· {eq.equipment_type}</span>
                    {locked && (
                      <span className="text-[10px] text-amber-400 ml-2 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {lockedReason(eq.id)}
                      </span>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {equipmentId && isLocked(equipmentId) && (
          <p className="text-[11px] text-amber-400 mt-1">{lockedReason(equipmentId)}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        {existingAllocation?.id && (
          <Button
            variant="outline"
            size="sm"
            onClick={remove}
            disabled={saving}
            data-testid="dryhire-remove-allocation-btn"
            className="text-destructive hover:text-destructive"
          >
            Remove
          </Button>
        )}
        {showOffHire && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOffHire?.(job)}
            data-testid="dryhire-picker-offhire-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Off-Hire…
          </Button>
        )}
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          className="btn-volt"
          onClick={save}
          disabled={saving || loading || !equipmentId}
          data-testid="dryhire-save-allocation-btn"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
          {existingAllocation?.id ? 'Update Asset' : 'Allocate Asset'}
        </Button>
      </div>
    </div>
      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(d => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        confirmText="Delete"
        variant="destructive"
      />
    </>
  );
}

export default DryHireAssetPicker;
